/**
 * Which way a language is written, and keeping inserted values readable
 * inside right-to-left text — docs/31-global-languages.md, "Right to left".
 *
 * Arabic, Hebrew, Persian and Urdu are written right to left (`rtl` in the
 * registry, locales.ts). Everything else DrBell speaks — English and every
 * Indian language included — is left to right, and stays exactly as it was:
 * nothing here changes a single character of a left-to-right string.
 *
 * Deliberately free of the catalogue, like `./locales`, so a website can
 * import it without shipping every string (`@drbell/i18n/direction`).
 */
import { localeDef } from './locales.js';

export type TextDirection = 'ltr' | 'rtl';

/** Written right to left. Unknown codes are not: English is the fallback for those. */
export function isRtlLocale(code: string | null | undefined): boolean {
  if (!code) return false;
  return localeDef(code)?.rtl ?? false;
}

/** `dir` for an element holding text in this language: `rtl` or `ltr`. */
export function localeDirection(code: string | null | undefined): TextDirection {
  return isRtlLocale(code) ? 'rtl' : 'ltr';
}

/*
 * Unicode directional isolates (UAX #9, since Unicode 6.3).
 *
 * A value dropped into a right-to-left sentence — a time, a phone number, a
 * medicine's name, a dose — is otherwise reordered by its neighbours: in an
 * Arabic sentence "+91 98765 43210" comes out as "43210 98765 91+", and
 * "Metformin (500 mg)." loses its brackets to the wrong side. An isolate
 * lays the value out on its own, then places it as one unit.
 *
 * FSI (first strong isolate) picks the value's own direction from its first
 * letter: Latin "8:00 AM" and "Metformin" read left to right, an Arabic name
 * right to left, and a value with no letters at all (a phone number, "8:00")
 * left to right. Invisible, and ignored by screen readers.
 */
export const FSI = '⁨';
export const LRI = '⁦';
export const RLI = '⁧';
export const PDI = '⁩';

const ISOLATES = /[⁦-⁩]/g;

/**
 * One value, laid out on its own. Empty stays empty.
 *
 * A value that already holds isolates is left as its maker built it: an
 * isolated value is not wrapped twice, and a list of isolated items
 * ("⁨Metformin 8:00⁩, ⁨Aspirin 9:00⁩") must flow with the sentence around
 * it — wrapped again, an FSI finds no letter outside the inner isolates and
 * would set the whole list left to right, first item on the left.
 */
export function isolate(value: string | number): string {
  const s = String(value);
  if (s === '' || /[⁦-⁨]/.test(s)) return s;
  return `${FSI}${s}${PDI}`;
}

/** Always left to right, whatever its letters: a phone number, a code, an arrow. */
export function isolateLtr(value: string | number): string {
  const s = String(value);
  return s === '' ? s : `${LRI}${s}${PDI}`;
}

/** Every value isolated, for a template in a right-to-left language. */
export function isolateValues<V extends Record<string, string | number>>(values: V): Record<keyof V, string> {
  const out = {} as Record<keyof V, string>;
  for (const k of Object.keys(values) as Array<keyof V>) out[k] = isolate(values[k]!);
  return out;
}

/**
 * The values to fill a template in `code` with: isolated for a right-to-left
 * language, untouched for every other. Untouched matters: an SMS in a
 * left-to-right language must not gain invisible characters (they would
 * cost a GSM-7 message its encoding).
 */
export function valuesFor<V extends Record<string, string | number>>(
  code: string | null | undefined,
  values: V,
): V | Record<keyof V, string> {
  return isRtlLocale(code) ? isolateValues(values) : values;
}

/** The text without isolates — to compare, copy or dial a value. */
export function withoutIsolates(text: string): string {
  return text.replace(ISOLATES, '');
}
